import {
  useBokaLink,
  useDefaultBokaLinkConfig,
} from "@/features/appointments/useDefaultBokaLinkConfig";
import { isCancelledStatus } from "@/features/appointments/utils";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { DropinAppointmentForm } from "@/features/tasks/taskModal/edit/DropinAppointmentForm";
import styled from "@emotion/styled";
import { type hfb as hfbType, useProfile } from "@repo/api";
import {
  Button,
  InlineMessage,
  ListView,
  ListViewItem,
  Variables,
  informationCircleIcon,
  linkOutIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { ElementsContainer, SectionContainer, useSettings } from "@repo/ui";
import { hasItems } from "@repo/utils";
import { useState } from "react";

const BokaLinkButton = ({
  cmpCode,
  service,
  previousAppointmentLink,
}: {
  cmpCode?: string;
  service?: string;
  previousAppointmentLink?: string | undefined;
}) => {
  const bokaLink = useBokaLink(cmpCode, service);
  const disabled = !previousAppointmentLink && !service;
  return (
    <Button
      size="small"
      type="secondary"
      text={t("action.book_appointment_in_boka")}
      href={disabled ? undefined : (previousAppointmentLink ?? bokaLink)}
      ssrIcon={linkOutIcon}
      iconPosition="trailing"
      newWindow
      fluid
      disabled={disabled}
    />
  );
};

const ScheduleAppointmentContent = styled(SectionContainer)`
  margin-top: ${Variables.space75};
`;

export const AppointmentStep = () => {
  const { calendar } = useSettings();
  const { pushModal, hideModal } = useModals();
  const { opportunity } = useActiveProject();
  const { data: profile } = useProfile();
  const { cmpCode } = useDefaultBokaLinkConfig();

  const [cmp, setCmp] = useState<string | undefined>(cmpCode);
  const [hfb, setHfb] = useState<hfbType>();
  const [service, setService] = useState<string>();

  const activeBokaAppointments =
    opportunity?.appointmentDetails
      ?.filter(
        (appt) =>
          appt.type === "BOKA_APPOINTMENT" &&
          !isCancelledStatus(appt.appointmentStatus)
      )
      .sort(
        (a, b) =>
          new Date(b.timeWindowStartTime).getTime() -
          new Date(a.timeWindowStartTime).getTime()
      ) || [];

  const isFirstAppointment = activeBokaAppointments.length === 0;

  return (
    <SectionContainer>
      <ListView id="book-appointment-modal_items">
        {opportunity?.id && (
          <ListViewItem
            showDivider={false}
            title={t("action.search_appointment")}
            control="navigational"
            onClick={() =>
              pushModal({
                type: "SEARCH_APPOINTMENT",
                projectId: opportunity?.id,
                initialAppointments: opportunity.appointmentDetails,
                onBack: () => {
                  hideModal("SEARCH_APPOINTMENT");
                },
              })
            }
          />
        )}
        {calendar?.drop_in && (
          <ListViewItem
            showDivider={false}
            title={t("label.drop_in")}
            control="navigational"
            onClick={() =>
              pushModal({
                type: "TASK",
                initialData: {
                  parentTaskType: "appointment",
                  subTaskType: "dropIn",
                  selectedCoworkers: profile
                    ? [profile.mail!]
                    : opportunity?.owner
                      ? [opportunity.owner]
                      : undefined,
                },
                opportunityId: opportunity?.id,
                onBack: () => {
                  hideModal("TASK");
                },
                context: "PROJECT",
              })
            }
          />
        )}
        <ListViewItem
          showDivider={false}
          title={
            isFirstAppointment
              ? t("action.book_appointment_in_boka")
              : t("action.book_followup_appointment_in_boka")
          }
          description={
            !isFirstAppointment && t("prompt.followup_appointment_description")
          }
          addOn={
            <ScheduleAppointmentContent>
              {!isFirstAppointment && (
                <InlineMessage
                  title={t("label.how_it_works")}
                  ssrIcon={informationCircleIcon}
                  body={t("prompt.followup_appointment_how_it_works")}
                />
              )}

              {isFirstAppointment && (
                <ElementsContainer>
                  <DropinAppointmentForm
                    cmpCode={cmp}
                    hfb={hfb}
                    service={service}
                    setCmpCode={setCmp}
                    setHfb={setHfb}
                    setService={setService}
                  />
                </ElementsContainer>
              )}
              <BokaLinkButton
                cmpCode={cmp}
                service={service}
                previousAppointmentLink={
                  hasItems(activeBokaAppointments)
                    ? activeBokaAppointments[0]?.appointmentLink
                    : undefined
                }
              />
            </ScheduleAppointmentContent>
          }
        />
      </ListView>
    </SectionContainer>
  );
};
